import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, CalendarDays, MapPin, Sparkles } from "lucide-react";
import { formatEventDate, type VREvent } from "@/hooks/useEvents";

interface Props {
  event: VREvent;
  index?: number;
}

const EventCard = ({ event, index = 0 }: Props) => {
  const date = formatEventDate(event.event_date);

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.35, delay: Math.min(index, 6) * 0.05 }}
      className="h-full"
    >
      <Link
        to={`/events/${event.slug}`}
        className="group flex h-full flex-col overflow-hidden rounded-2xl border border-border bg-card transition-all duration-300 hover:-translate-y-1 hover:border-primary/40 hover:shadow-lg"
      >
        <div className="relative aspect-[16/10] overflow-hidden">
          {event.cover_image ? (
            <img
              src={event.cover_image}
              alt={event.title}
              loading="lazy"
              className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
          ) : (
            <div className="flex h-full w-full flex-col items-center justify-center gap-2 bg-gradient-to-br from-primary/15 via-background to-secondary/15">
              <Sparkles className="h-6 w-6 text-primary" strokeWidth={1.5} />
              <span className="font-heading text-sm font-bold tracking-tight text-foreground">
                VR<span className="text-primary">Rentals</span>
              </span>
            </div>
          )}
          {event.category && (
            <span className="absolute left-4 top-4 rounded-full border border-border/60 bg-background/80 px-3 py-1 font-body text-[11px] uppercase tracking-[0.18em] text-foreground backdrop-blur">
              {event.category}
            </span>
          )}
        </div>

        <div className="flex flex-1 flex-col p-6">
          <h3 className="font-heading text-lg font-bold text-foreground transition-colors group-hover:text-primary">
            {event.title}
          </h3>

          {(date || event.location) && (
            <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 font-body text-xs text-muted-foreground">
              {date && (
                <span className="inline-flex items-center gap-1.5">
                  <CalendarDays className="h-3.5 w-3.5" /> {date}
                </span>
              )}
              {event.location && (
                <span className="inline-flex items-center gap-1.5">
                  <MapPin className="h-3.5 w-3.5" /> {event.location}
                </span>
              )}
            </div>
          )}

          {event.short_description && (
            <p className="mt-4 font-body text-sm leading-relaxed text-muted-foreground line-clamp-3">
              {event.short_description}
            </p>
          )}

          <span className="mt-6 inline-flex items-center gap-2 font-heading text-sm font-semibold text-primary">
            View Event
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </span>
        </div>
      </Link>
    </motion.div>
  );
};

export default EventCard;
